import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../authConfig";
import { createLogger } from "../helper/logger";

const useAuthenticatedPage = () => {
	const logger = useMemo(() => createLogger("useAuthenticatedPage"), []);
	const { instance, accounts, inProgress } = useMsal();
	const realIsAuthenticated = useIsAuthenticated();
	const navigate = useNavigate();
	const {
		REACT_APP_AUTH_USERS_GROUP,
		REACT_APP_AUTH_ADMIN_GROUP,
		REACT_APP_AUTH_FAKE_IT,
	} = process.env;
	const useFakeAuth =
		REACT_APP_AUTH_FAKE_IT === "TRUE" || REACT_APP_AUTH_FAKE_IT === "YES";

	// Override authentication state when in fake mode
	const isAuthenticated = useFakeAuth ? true : realIsAuthenticated;

	const [userRole, setUserRole] = useState(useFakeAuth ? "ADMIN" : "NONE");
	const [userName, setUserName] = useState(
		useFakeAuth ? "FakeUser@example.com" : "None",
	);

	// Create fake user account for development
	const fakeUserAccount = useFakeAuth
		? {
				username: "FakeUser@example.com",
				name: "Fake Developer",
				localAccountId: "fake-account-id",
				homeAccountId: "fake-home-account-id",
			}
		: null;

	useEffect(() => {
		if (!useFakeAuth && !isAuthenticated && inProgress === "none") {
			navigate("/");
			return;
		}

		// Skip the real authentication flow if using fake auth
		if (useFakeAuth) {
			setUserRole("ADMIN"); // Default to admin for easy testing
			setUserName("FakeUser@example.com");
			return;
		}

		if (isAuthenticated) {
			instance
				.acquireTokenSilent({
					...loginRequest,
					scopes: ["User.Read", "GroupMember.Read.All"],
					account: accounts[0],
				})
				.then(async (response) => {
					try {
						// Fetch user's groups using Microsoft Graph API
						const graphResponse = await fetch(
							"https://graph.microsoft.com/v1.0/me/transitiveMemberOf?$select=id,displayName",
							{
								headers: {
									Authorization: `Bearer ${response.accessToken}`,
									"Content-Type": "application/json",
								},
							},
						);

						if (!graphResponse.ok) {
							throw new Error("Failed to fetch user groups");
						}

						const groupData = await graphResponse.json();
						const groups = groupData.value.map((group) => group.displayName);
						if (
							!groups.includes(REACT_APP_AUTH_USERS_GROUP) &&
							!groups.includes(REACT_APP_AUTH_ADMIN_GROUP)
						) {
							logger.error("User does not belong to required group", {
								userGroups: groups,
								requiredUserGroup: REACT_APP_AUTH_USERS_GROUP,
								requiredAdminGroup: REACT_APP_AUTH_ADMIN_GROUP,
							});
							throw new Error("User does not belong to the required group");
						}

						instance.setActiveAccount(accounts[0]);
						setUserRole(
							groups.includes(REACT_APP_AUTH_ADMIN_GROUP) ? "ADMIN" : "USER",
						);
						setUserName(accounts[0].username);
					} catch (error) {
						logger.error("Error in group validation", {
							errorMessage: error.message,
							stack: error.stack,
						});
						navigate("/login-error");
					}
				})
				.catch((error) => {
					logger.error("Authentication error", {
						errorMessage: error.message,
						stack: error.stack,
					});
					navigate("/login-error");
				});
		}
	}, [
		instance,
		logger,
		accounts,
		navigate,
		isAuthenticated,
		REACT_APP_AUTH_USERS_GROUP,
		REACT_APP_AUTH_ADMIN_GROUP,
		useFakeAuth,
		inProgress,
	]);

	const shouldBlockPage =
		!useFakeAuth && !isAuthenticated && inProgress === "none";
	// Add debug indicator for fake auth mode
	if (useFakeAuth) {
		logger.info("🔑 USING FAKE AUTHENTICATION - Development Mode");
	}

	logger.debug("Authentication state", {
		shouldBlockPage,
		useFakeAuth,
		isAuthenticated,
		inProgress,
	});

	return {
		shouldBlockPage,
		userData: useFakeAuth ? fakeUserAccount : accounts?.[0] || null,
		userRole,
		userName,
		inProgress,
		isFakeAuth: useFakeAuth,
	};
};

export default useAuthenticatedPage;
